import {
  faAudioDescription as faAudioDescriptionSolid,
  faClosedCaptioning as faClosedCaptioningSolid,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faArrowRotateLeft,
  faArrowRotateRight,
  faClosedCaptioning,
  faExpand,
  faCompress,
  faVolumeUp,
  faVolumeMute,
  faAudioDescription,
} from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlay, faCirclePause } from '@fortawesome/sharp-solid-svg-icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function PlayerButtons(props) {
  const { t } = useTranslation();
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const [previousVolume, setPreviousVolume] = useState(1);
  const [timeCurrentNumber, setTimeCurrentNumber] = useState('');

  useEffect(() => {
    if (props.currentTime !== false && props.currentTime !== undefined) {
      const {
        hours: currentHours,
        minutes: currentMinutes,
        seconds: currentSeconds,
      } = formatTime(props.currentTime);

      setTimeCurrentNumber(
        Number(totalHours) > 0
          ? `${currentHours}:${currentMinutes}:${currentSeconds}`
          : `${currentMinutes}:${currentSeconds}`
      );
    }
  }, [props.currentTime, props.duration]);

  const handleSkip = (seconds) => {
    const media = props.mediaRef.current;
    media.currentTime += seconds;
    props.setCurrentTime(media.currentTime);
  };

  const togglePlay = () => {
    let carryOn = true;
    if (props.mediaContent?.waiver === true) {
      carryOn = props.checkForConsent();
    }
    if (carryOn) {
      props.setIsPlaying(!props.isPlaying);
      const media = props.mediaRef.current;
      if (props.isPlaying) {
        media.pause();
        props.runCounter(false);
      } else {
        media.play();
        props.runCounter(true);
      }
    }
  };

  const toggleMute = (e) => {
    const media = props.mediaRef.current;
    const isFromInput = e?.target?.tagName === 'INPUT';
    const isArrowKey = e?.key === 'ArrowLeft' || e?.key === 'ArrowRight';
    const isMuteKey = e?.key === 'Enter' || e?.key === ' ';

    if (isFromInput && (isArrowKey || !isMuteKey)) {
      return;
    }

    if (!props.isMuted) {
      setPreviousVolume(media.volume);
      media.volume = 0;
    } else {
      media.volume = previousVolume;
    }

    props.setIsMuted(!props.isMuted);
  };

  const handleToggleFullScreen = () => {
    const container = props.containerRef.current;
    if (props.isFullScreen) {
      document.exitFullscreen();
      if (props.mediaHeight) {
        const video = document.getElementsByTagName('video');
        video[0].style.height = `${props.mediaHeight}px`;
      }
    } else {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.mozRequestFullScreen) {
        /* Firefox */
        container.mozRequestFullScreen();
      } else if (container.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        /* IE/Edge */
        container.msRequestFullscreen();
      }
      const video = document.getElementsByTagName('video');
      video[0].style.height = '100%';
    }
    props.setIsFullScreen(!props.isFullScreen);
  };

  const formatTime = (timeInSeconds) => {
    const result = new Date(timeInSeconds * 1000).toISOString().substring(11, 19);
    return {
      hours: result.substring(0, 2),
      minutes: result.substring(3, 5),
      seconds: result.substring(6, 8),
    };
  };

  const {
    hours: currentHours,
    minutes: currentMinutes,
    seconds: currentSeconds,
  } = formatTime(props.currentTime);

  const {
    hours: totalHours,
    minutes: totalMinutes,
    seconds: totalSeconds,
  } = formatTime(props.duration);

  const progressBarTime =
    Number(totalHours) > 0
      ? `${currentHours} ${t('player.progress.bar.time.hours')} ${currentMinutes} ${t('player.progress.bar.time.minutes')} ${currentSeconds} ${t('player.progress.bar.time.seconds')}`
      : `${currentMinutes} ${t('player.progress.bar.time.minutes')} ${currentSeconds} ${t('player.progress.bar.time.seconds')}`;

  const progressBarTimeTotal =
    Number(totalHours) > 0
      ? `${totalHours} ${t('player.progress.bar.time.hours')} ${totalMinutes} ${t('player.progress.bar.time.minutes')} ${totalSeconds} ${t('player.progress.bar.time.seconds')}`
      : `${totalMinutes} ${t('player.progress.bar.time.minutes')} ${totalSeconds} ${t('player.progress.bar.time.seconds')}`;

  const timeTotalNumber =
    Number(totalHours) > 0
      ? `${totalHours}:${totalMinutes}:${totalSeconds}`
      : `${totalMinutes}:${totalSeconds}`;

  const handleADButtonClick = () => {
    props.isAdTurnedOn ? props.openSecondModal() : props.openFirstModal();
  };

  return (
    <div className="player-buttons">
      <div className="top-row">
        <div className="captions">
          <button
            type="button"
            className={`cc ${props.class}`}
            onClick={props.toggleCaptions}
            disabled={props.isAdTurnedOn}
            aria-pressed={props.showCaptions}
          >
            <span className="visuallyHidden">
              {t('M089')}{' '}
              {!props.isAdTurnedOn &&
                t('button.state.description', {
                  state: props.showCaptions ? t('button.state.off') : t('button.state.on'),
                })}
            </span>
            <FontAwesomeIcon
              icon={props.showCaptions ? faClosedCaptioningSolid : faClosedCaptioning}
            />
          </button>
          {props.showAD && (
            <button
              type="button"
              className={`ad ${props.class}`}
              onClick={handleADButtonClick}
              aria-pressed={props.isAdTurnedOn}
            >
              <span className="visuallyHidden">
                {t('player.ad.label')}{' '}
                {t('button.state.description', {
                  state: props.isAdTurnedOn ? t('button.state.off') : t('button.state.on'),
                })}
              </span>
              <FontAwesomeIcon
                icon={props.isAdTurnedOn ? faAudioDescriptionSolid : faAudioDescription}
              />
            </button>
          )}
        </div>
        <div className="play">
          <button
            type="button"
            className={`skip-backward ${props.class}`}
            onClick={() => handleSkip(-10)}
            aria-labelledby="skip-backward-icon"
          >
            <span id="skip-backward-icon" className="visuallyHidden">
              {t('M090')}
            </span>
            <FontAwesomeIcon icon={faArrowRotateLeft} />
          </button>
          <button
            type="button"
            className={`play-pause  ${props.class}`}
            onClick={togglePlay}
            aria-labelledby="play-pause-icon"
          >
            <span id="play-pause-icon" className="visuallyHidden">
              {props.isPlaying ? t('M092') : t('M030')}
            </span>
            <FontAwesomeIcon icon={props.isPlaying ? faCirclePause : faCirclePlay} />
          </button>
          <button
            type="button"
            className={`skip-forward ${props.class}`}
            onClick={() => handleSkip(10)}
            aria-labelledby="skip-forward-icon"
          >
            <span id="skip-forward-icon" className="visuallyHidden">
              {t('M091')}
            </span>
            <FontAwesomeIcon icon={faArrowRotateRight} className="icon" />
          </button>
        </div>

        <div className="volume">
          {!isIOS && (
            <div className={`volume-container ${props.mediaContent.type}`}>
              <button
                type="button"
                className={`mute ${props.class}`}
                onClick={toggleMute}
                aria-pressed={props.isMuted}
                aria-labelledby="mute-state-label"
              >
                <FontAwesomeIcon
                  icon={props.isMuted ? faVolumeMute : faVolumeUp}
                  className={props.isMuted ? 'mute-icon' : ''}
                />
                <span id="mute-state-label" className="visuallyHidden">
                  {props.isMuted ? t('player.volume.muted') : t('player.volume.volume')}
                </span>
              </button>
              <div className="volume-bar">
                <input
                  aria-label={t('player.volume.label')}
                  aria-valuemin={0}
                  aria-valuemax={1}
                  aria-valuenow={props.isMuted ? 0 : props.volume}
                  aria-valuetext={`${Math.round((props.isMuted ? 0 : props.volume) * 100)}%`}
                  tabIndex="0"
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={props.isMuted ? 0 : props.volume}
                  onChange={(e) => {
                    const media = props.mediaRef.current;
                    media.volume = e.target.value;
                    props.setVolume(media.volume);
                    if (props.isMuted) {
                      props.setIsMuted(false);
                    }
                  }}
                  onKeyDown={(e) => toggleMute(e)}
                />
              </div>
            </div>
          )}
          {props.mediaContent.type !== 'audio' && (
            <button type="button" className="fullscreen-player" onClick={handleToggleFullScreen}>
              <span id="fullscreen-state-label" className="visuallyHidden">
                {props.isFullScreen ? t('M096') : t('M095')}
              </span>
              <FontAwesomeIcon icon={props.isFullScreen ? faCompress : faExpand} />
            </button>
          )}
        </div>
      </div>
      <div
        className="progress"
        aria-label={`${progressBarTime} ${t('player.progress.bar.of')} ${progressBarTimeTotal} ${t('player.progress.bar.time.of.progress')}`}
      >
        <span className="time-current">{timeCurrentNumber}</span>
        <span>/</span>
        <span className="time-total">{timeTotalNumber}</span>
        <span className="visuallyHidden">{t('player.progress.bar.time.of.progress')}</span>
        <div className="progress-bar-wrapper">
          <input
            type="range"
            className="progress-bar"
            aria-label={t('player.progress.bar.label')}
            aria-valuemin={0}
            aria-valuemax={props.duration}
            aria-valuenow={props.currentTime}
            aria-valuetext={`${progressBarTime} ${t('player.progress.bar.of')} ${progressBarTimeTotal}`}
            min={0}
            max={props.duration}
            step={1}
            value={props.currentTime}
            onChange={(e) => {
              const media = props.mediaRef.current;
              media.currentTime = Number(e.target.value);
              props.setCurrentTime(media.currentTime);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                handleSkip(-5);
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                handleSkip(5);
              }
            }}
          />
          <div
            className="progress-bar-fill"
            style={{ width: `${(props.currentTime / props.duration) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
